import React, { useCallback, useEffect, useRef, useState } from 'react';
import { get, patchWithAuth, postWithAuth } from '../../utils/apiClient';
import DoseSpotDialog, { openDoseSpotWindow } from './DoseSpotDialog';
import MedicationHistoryReview from '../notes/components/MedicationHistoryReview';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { vendorRxStatusLabel } from '../../utils/medicationLabels';
import { doseSpotErrorMessage } from './doseSpotErrorMessage';

interface PrescriptionActionLauncherProps {
  patientId: string;
  noteId?: string;
  canPrescribe: boolean;
  onMedicationsRefreshed?: () => Promise<void> | void;
}

interface PrescriptionAction {
  id: string;
  action_type: string;
  status: string;
  clinical_reason: string;
  before?: PrescriptionSnapshot | null;
  after?: PrescriptionSnapshot | null;
  prescription?: {
    medication_name: string;
    dosage: string;
    frequency: string;
    route?: string | null;
    quantity?: string | null;
    directions?: string | null;
    pharmacy?: string | null;
    status: string;
  } | null;
}

interface PrescriptionSnapshot {
  medication_name: string;
  dosage: string;
  frequency: string;
  route?: string | null;
  quantity?: string | null;
  refills?: number | null;
  directions?: string | null;
  pharmacy?: string | null;
  status: string;
  vendor_medication_status?: string | null;
  date_inactive?: string | null;
}

const RECONCILIATION_INTERVAL_MS = 10_000;
const RECONCILIATION_WINDOW_MS = 120_000;

function PrescriptionActionLauncher({
  patientId,
  noteId,
  canPrescribe,
  onMedicationsRefreshed,
}: PrescriptionActionLauncherProps) {
  const [doseSpotOpen, setDoseSpotOpen] = useState(false);
  const [launchError, setLaunchError] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [prescriberReady, setPrescriberReady] = useState(false);
  const [mobileLaunchActive, setMobileLaunchActive] = useState(false);
  const [actions, setActions] = useState<PrescriptionAction[]>([]);
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [savingReason, setSavingReason] = useState<string | null>(null);
  const [plannedActionType, setPlannedActionType] = useState('change');
  const [plannedReason, setPlannedReason] = useState('');
  const [planning, setPlanning] = useState(false);
  const reconciliationStartedAt = useRef<number | null>(null);
  const reconciliationInFlight = useRef(false);
  const sessionId = useRef<string | null>(null);
  const isMobile = useIsMobile();

  const loadActions = useCallback(async () => {
    if (!noteId) return;
    try {
      const response = await get(`/erx/prescription-actions/note/${noteId}`);
      if (response.ok) {
        const data = await response.json();
        setActions(data.actions || []);
      }
    } catch {
      // Medication synchronization remains available if documentation cannot load.
    }
  }, [noteId]);

  useEffect(() => {
    void loadActions();
  }, [loadActions]);

  useEffect(() => {
    if (!canPrescribe) return undefined;
    let cancelled = false;
    void get('/erx/prescriber/status')
      .then(async (response) => {
        if (!response.ok) return;
        const status = await response.json();
        const ready = (
          status.can_prescribe === true
          && status.enrolled === true
          && status.dosespot_seat_assigned === true
        );
        if (!cancelled) setPrescriberReady(ready);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [canPrescribe]);

  const reconcile = useCallback(async () => {
    if (!patientId || reconciliationInFlight.current) return;
    reconciliationInFlight.current = true;
    setSyncing(true);
    try {
      const response = await postWithAuth(`/erx/prescriptions/${patientId}/sync`, {
        session_id: sessionId.current,
        note_id: noteId || null,
      });
      if (!response.ok) {
        const data = typeof response.json === 'function'
          ? await response.json().catch(() => null)
          : null;
        throw new Error(doseSpotErrorMessage(data, 'Unable to refresh medications from DoseSpot'));
      }
      const data = typeof response.json === 'function' ? await response.json().catch(() => null) : null;
      if (Array.isArray(data?.actions)) setActions(data.actions);
      await loadActions();
      await onMedicationsRefreshed?.();
      setLaunchError('');
    } catch (cause) {
      setLaunchError(cause instanceof Error ? cause.message : 'Unable to refresh medications from DoseSpot');
    } finally {
      reconciliationInFlight.current = false;
      setSyncing(false);
    }
  }, [loadActions, noteId, onMedicationsRefreshed, patientId]);

  const launchDoseSpot = () => {
    setLaunchError('');
    if (isMobile) {
      setMobileLaunchActive(true);
      void openDoseSpotWindow(patientId).catch((cause: unknown) => {
        setLaunchError(cause instanceof Error ? cause.message : 'Unable to open DoseSpot');
        setMobileLaunchActive(false);
      });
      return;
    }
    setDoseSpotOpen(true);
  };

  const openDoseSpot = () => {
    const nextSessionId = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
    sessionId.current = nextSessionId;
    launchDoseSpot();
  };

  const savePlanAndOpen = async () => {
    const nextSessionId = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
    sessionId.current = nextSessionId;
    setPlanning(true);
    try {
      const response = await postWithAuth(`/erx/prescription-actions/${patientId}`, {
        action_type: plannedActionType,
        clinical_reason: plannedReason.trim(),
        session_id: nextSessionId,
        note_id: noteId,
      });
      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(doseSpotErrorMessage(data, 'Unable to save the planned medication change'));
      }
      setPlannedReason('');
      launchDoseSpot();
    } catch (cause) {
      setLaunchError(
        cause instanceof Error ? cause.message : 'Unable to save the planned medication change',
      );
    } finally {
      setPlanning(false);
    }
  };

  useEffect(() => {
    if (!doseSpotOpen && !mobileLaunchActive) return undefined;
    reconciliationStartedAt.current = Date.now();
    void reconcile();

    const reconcileIfActive = () => {
      const startedAt = reconciliationStartedAt.current;
      if (startedAt !== null && Date.now() - startedAt < RECONCILIATION_WINDOW_MS) {
        void reconcile();
      }
    };
    const intervalId = window.setInterval(reconcileIfActive, RECONCILIATION_INTERVAL_MS);
    window.addEventListener('focus', reconcileIfActive);
    document.addEventListener('visibilitychange', reconcileIfActive);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', reconcileIfActive);
      document.removeEventListener('visibilitychange', reconcileIfActive);
      reconciliationStartedAt.current = null;
    };
  }, [doseSpotOpen, mobileLaunchActive, reconcile]);

  const canOpenEPrescribe = canPrescribe && prescriberReady;

  return (
    <>
      {canOpenEPrescribe && (
        <div className="mt-4 rounded-lg border border-indigo-200 bg-indigo-50 p-4 dark:border-indigo-800 dark:bg-indigo-950">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-base font-semibold text-indigo-950 dark:text-indigo-100">Prescribing</h3>
              <p className="mt-1 text-sm text-indigo-800 dark:text-indigo-200">
                Prescribe, change, or discontinue medications in DoseSpot.
              </p>
            </div>
            <button
              type="button"
              onClick={openDoseSpot}
              disabled={planning}
              className="min-h-[44px] rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              {planning ? 'Saving plan...' : 'Open DoseSpot'}
            </button>
          </div>
          {noteId && (
            <div className="mt-3 rounded-md border border-indigo-200 bg-white/70 p-3 dark:border-indigo-700 dark:bg-indigo-900/40">
              <p className="font-medium text-indigo-950 dark:text-indigo-100">
                Plan this medication action in the note
              </p>
              <p className="mt-1 text-sm text-indigo-800 dark:text-indigo-200">
                Optional: record the intended action and clinical reason before entering DoseSpot.
              </p>
              <div className="mt-2 grid gap-2 sm:grid-cols-[10rem_1fr_auto] sm:items-end">
                <label className="text-sm font-medium">
                  Action
                  <select
                    className="mt-1 block min-h-[44px] w-full rounded border border-indigo-300 bg-white px-2 text-gray-900"
                    value={plannedActionType}
                    onChange={(event) => setPlannedActionType(event.target.value)}
                  >
                    <option value="add">Add</option>
                    <option value="change">Change</option>
                    <option value="stop">Stop</option>
                  </select>
                </label>
                <label className="text-sm font-medium">
                  Planned clinical reason
                  <textarea
                    className="mt-1 block min-h-[88px] w-full rounded border border-indigo-300 bg-white px-2 py-2 text-gray-900"
                    value={plannedReason}
                    onChange={(event) => setPlannedReason(event.target.value)}
                    placeholder="What do you plan to change and why?"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => void savePlanAndOpen()}
                  disabled={planning || !plannedReason.trim()}
                  className="min-h-[44px] rounded border border-indigo-600 bg-white px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
                >
                  {planning ? 'Saving...' : 'Save plan & open'}
                </button>
              </div>
            </div>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => void reconcile()}
              disabled={syncing}
              className="min-h-[44px] rounded-md border border-indigo-600 bg-white px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
            >
              {syncing ? 'Refreshing medications...' : 'Refresh medications'}
            </button>
            {syncing && <span className="text-sm text-indigo-700">Syncing DoseSpot changes…</span>}
          </div>
          {launchError && <p className="mt-2 text-sm text-red-700" role="alert">{launchError}</p>}
        </div>
      )}
      {actions.length > 0 && (
        <section className="mt-4 space-y-3" aria-label="DoseSpot medication action documentation">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">
            Medication changes from DoseSpot
          </h3>
          {actions.map((action) => (
            <div
              key={action.id}
              className="rounded-lg border border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-950 dark:border-indigo-800 dark:bg-indigo-950 dark:text-indigo-100"
            >
              <p className="font-semibold">
                {action.action_type[0].toUpperCase() + action.action_type.slice(1)} medication
                {' · '}
                {action.status === 'completed_needs_reason' ? 'Needs provider reason' : action.status}
              </p>
              {action.prescription && (
                <div className="mt-2 space-y-1">
                  <p className="font-medium">
                    {action.prescription.medication_name} — {action.prescription.dosage}
                  </p>
                  <p>
                    {action.prescription.frequency}
                    {action.prescription.route ? ` · ${action.prescription.route}` : ''}
                    {action.prescription.quantity ? ` · Quantity ${action.prescription.quantity}` : ''}
                    {` · ${vendorRxStatusLabel(action.prescription.status)}`}
                  </p>
                  {action.prescription.directions && <p>{action.prescription.directions}</p>}
                </div>
              )}
              {(action.before || action.after) && (
                <div className="mt-3 rounded border border-indigo-200 bg-white/70 p-3 text-sm dark:border-indigo-700 dark:bg-indigo-900/40">
                  <p className="font-semibold">What changed in DoseSpot</p>
                  {action.before && (
                    <p className="mt-1">
                      <span className="font-medium">Before:</span>{' '}
                      {action.before.medication_name} — {action.before.dosage} · {action.before.frequency}
                      {action.before.quantity ? ` · Quantity ${action.before.quantity}` : ''}
                      {action.before.directions ? ` · ${action.before.directions}` : ''}
                      {` · ${vendorRxStatusLabel(action.before.status)}`}
                      {action.before.vendor_medication_status
                        ? ` · ${action.before.vendor_medication_status}`
                        : ''}
                    </p>
                  )}
                  {action.after && (
                    <p className="mt-1">
                      <span className="font-medium">After:</span>{' '}
                      {action.after.medication_name} — {action.after.dosage} · {action.after.frequency}
                      {action.after.quantity ? ` · Quantity ${action.after.quantity}` : ''}
                      {action.after.directions ? ` · ${action.after.directions}` : ''}
                      {` · ${vendorRxStatusLabel(action.after.status)}`}
                      {action.after.vendor_medication_status
                        ? ` · ${action.after.vendor_medication_status}`
                        : ''}
                    </p>
                  )}
                </div>
              )}
              {canPrescribe ? (
                <>
                  <label className="mt-3 block font-medium" htmlFor={`prescription-action-reason-${action.id}`}>
                    Why did you make this change?
                  </label>
                  <textarea
                    id={`prescription-action-reason-${action.id}`}
                    className="mt-1 min-h-[88px] w-full rounded border border-indigo-300 bg-white p-2 text-sm text-gray-900"
                    value={reasons[action.id] ?? action.clinical_reason}
                    onChange={(event) => setReasons((current) => ({
                      ...current,
                      [action.id]: event.target.value,
                    }))}
                    placeholder="Document the clinical reason for adding, changing, or stopping this medication."
                  />
                  <button
                    type="button"
                    className="mt-2 min-h-[44px] rounded bg-indigo-600 px-3 py-2 font-medium text-white disabled:opacity-50"
                    disabled={savingReason === action.id || !(reasons[action.id] ?? action.clinical_reason).trim()}
                    onClick={async () => {
                      setSavingReason(action.id);
                      try {
                        const response = await patchWithAuth(`/erx/prescription-actions/${action.id}/reason`, {
                          clinical_reason: (reasons[action.id] ?? action.clinical_reason).trim(),
                        });
                        if (response.ok) await loadActions();
                      } finally {
                        setSavingReason(null);
                      }
                    }}
                  >
                    {savingReason === action.id ? 'Saving reason...' : 'Save reason'}
                  </button>
                </>
              ) : (
                <p className="mt-3 text-sm text-indigo-800">
                  Medication changes are view-only for non-prescribers.
                </p>
              )}
            </div>
          ))}
        </section>
      )}
      <MedicationHistoryReview
        patientId={patientId}
        onMedicationsRefreshed={onMedicationsRefreshed}
      />
      <DoseSpotDialog
        clientId={patientId}
        open={doseSpotOpen}
        onClose={() => {
          setDoseSpotOpen(false);
          setMobileLaunchActive(false);
          void reconcile();
        }}
      />
    </>
  );
}

export default PrescriptionActionLauncher;
